<template>
  <div>
    <Layout :pagetitle="$t('Users')">
      <form autocomplete="off" @submit.prevent="saveUser">
        <b-card>
          <b-row>
            <b-col>
              <h4>{{ $t('User') }}</h4>
            </b-col>
          </b-row>
          <br />
          <b-row>
            <b-col md="6">
              <label>{{ $t('Name') }}</label>
              <b-form-input :trim="true" autocomplete="off" required v-model="name" />
            </b-col>
            <b-col md="6">
              <label>E-mail</label>
              <b-form-input :trim="true" autocomplete="off" required v-model="email" />
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <label>{{ $t('Brand') }}</label>
              <brandSelectorUnique :modelValue="brand" @onChange="setBrand" />
            </b-col>
            <b-col md="6">
              <label>{{ $t('Language') }}</label>
              <languageSelector :modelValue="language" @onChange="setLanguage" />
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <label>{{ $t('Password') }}</label>
              <b-form-input
                :trim="true"
                autocomplete="off"
                :required="!$route.params.id"
                type="password"
                v-model="password"
              />
            </b-col>
            <b-col md="6">
              <label>{{ $t('Password Confirmation') }}</label>
              <b-form-input
                :trim="true"
                autocomplete="off"
                :required="!$route.params.id"
                type="password"
                v-model="passwordConfirmation"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{ $t('Roles') }}</label>
              <userRolesSelector :modelValue="roles" @onChange="setRoles" />
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{ $t('Sub Brands') }}</label>
              <subBrandsSelector :modelValue="subBrands" @onChange="setSubBrands" />
            </b-col>
          </b-row>
        </b-card>
        <b-row>
          <div>
            <button
              type="submit"
              class="btn btn-primary m-1"
              v-if="$route.params.id"
              :disabled="$store.state.isLoading"
            >
              {{ $t('Update') }}
            </button>
            <button type="submit" class="btn btn-primary m-1" :disabled="$store.state.isLoading" v-else>
              {{ $t('Create') }}
            </button>
            <button type="button" class="btn btn-secondary m-1" @click="$router.back()">
              {{ $t('Back') }}
            </button>
          </div>
        </b-row>
      </form>
    </Layout>
  </div>
</template>

<script>
import Layout from '@/views/layouts/main';
import brandSelectorUnique from '@/components/selectors/brandSelectorUnique';
import languageSelector from '@/components/selectors/languageSelector';
import subBrandsSelector from '@/components/selectors/subBrandsSelector';
import userRolesSelector from '@/components/selectors/userRolesSelector';
import authService from '@/services/auth.services';

export default {
  components: { Layout, brandSelectorUnique, languageSelector, userRolesSelector, subBrandsSelector },
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      email: '',
      name: '',
      idAuthentication: null,
      brand: null,
      allowedBrands: [],
      company: null,
      language: 'pt',
      roles: [],
      subBrands: []
    };
  },
  mounted() {
    if (this.$route.params?.id) {
      this.getUser();
    }
  },
  methods: {
    saveUser() {
      if (this.$route.params?.id) {
        this.updateUser();
      } else {
        this.createUser();
      }
    },
    getUser() {
      const user_id = this.$route.params.id;
      this.$store.commit('SET_LOADING', true);

      this.$api
        .get(`api/user/${user_id}`, {})
        .then((res) => {
          this.$store.commit('SET_LOADING', false);
          let user = res.data;

          this.email = user.email;
          this.name = user.name;
          this.allowedBrands = user.allowed_brands;
          this.brand = user.allowed_brands[0];
          this.roles = user?.roles || [];
          this.idAuthentication = user.id_authentication;
          if (user.settings?.language) {
            this.language = user.settings.language;
          }
          if (user.settings?.subBrands) {
            this.subBrands = user.settings.subBrands;
          }
        })
        .catch((err) => {
          this.$store.commit('SET_LOADING', false);
          console.log(err);
          this.$customAlert({
            type: 'error',
            msg: this.$t('Error')
          });
        });
    },
    createUser() {
      if (!this.validateUser()) return;
      let user = {
        name: this.name,
        email: this.email,
        password: this.password,
        allowedBrands: this.allowedBrands,
        settings: {
          language: this.language,
          subBrands: this.subBrands
        },
        roles: this.roles
      };

      this.$store.commit('SET_LOADING', true);

      this.$api
        .post('api/user', user)
        .then(() => {
          this.$store.commit('SET_LOADING', false);
          this.$customAlert({
            type: 'success',
            msg: this.$t('Success')
          });

          this.$router.push({
            name: 'userList'
          });
        })
        .catch((err) => {
          this.$store.commit('SET_LOADING', false);
          this.$customAlert({
            type: 'error',
            msg: err?.response?.data?.message || this.$t('Create user error')
          });
        });
    },
    updateUser() {
      if (!this.validateUser()) return;
      let user_id = this.$route.params.id;
      let user = {
        name: this.name,
        email: this.email,
        password: this.password,
        allowedBrands: this.allowedBrands,
        idAuthentication: this.idAuthentication,
        settings: {
          language: this.language,
          subBrands: this.subBrands
        },
        roles: this.roles
      };

      if (this.password != this.passwordConfirmation) {
        this.$customAlert({
          msg: this.$t('The password confirmation must be equal to the password'),
          type: 'error'
        });
      }

      if (this.$route.params.id && !this.passwordConfirmation) delete user.password;

      this.$store.commit('SET_LOADING', true);
      this.$api
        .put(`api/user/${user_id}`, user)
        .then(() => {
          this.$store.commit('SET_LOADING', false);
          this.$customAlert({
            type: 'success',
            msg: this.$t('Success')
          });
          this.$router.push({
            name: 'userList'
          });
        })
        .catch((err) => {
          this.$store.commit('SET_LOADING', false);
          console.log(err);
          this.$customAlert({
            type: 'error',
            msg: err?.response?.data?.message || this.$t('Update user error')
          });
        });
    },
    validateUser() {
      const editUser = this.$route.params.id && (!this.password || !this.passwordConfirmation);

      if (!editUser && !this.$utils.validatePassword(this.password)) {
        return false;
      }

      const usr = authService.getLoggedUser();

      if (usr?.settings?.subBrands?.length) {
        if (!this.subBrands?.length) {
          this.$customAlert({
            msg: this.$t('Select') + ': ' + this.$t('Sub Brand'),
            type: 'error'
          });
          return false;
        }
      }

      if (this.password != this.passwordConfirmation) {
        this.$customAlert({
          msg: this.$t('The password confirmation must be equal to the password'),
          type: 'error'
        });
        return false;
      }
      if (!this.allowedBrands.length) {
        this.$customAlert({
          msg: this.$t('Invalid brand'),
          type: 'error'
        });
        return false;
      }
      if (!this.language) {
        this.$customAlert({
          msg: this.$t('Invalid language'),
          type: 'error'
        });
        return false;
      }
      if (this.email) {
        if (!this.$utils.emailValidation(this.email) || !this.$utils.isValidEmail(this.email)) {
          this.$customAlert({
            type: 'error',
            msg: this.$t('Type a valid e-mail')
          });
          return false;
        }
      }
      return true;
    },
    setCompany(company = null) {
      this.company = company;
      this.setBrands();
    },
    setBrand(brand) {
      this.allowedBrands = [brand];
    },
    setLanguage(lang = 'en') {
      this.language = lang;
    },
    setRoles(roles = []) {
      this.roles = roles;
    },
    setSubBrands(subBrands = []) {
      this.subBrands = subBrands;
    }
  }
};
</script>

<style></style>
